export type ReviewDraft = {
  id: string;
  sheetRow?: number;
  reportingMonth?: string;
  ownerName: string;
  stockName: string;
  accountName: string;
  paymentDate: string;
  grossDividend: number;
  tax: number;
  netAmount: number;
  currency: "KRW" | "USD";
  notes: string;
  sourceText: string;
  selected: boolean;
  reviewReason: string;
};

const number = (value: string) => Number(value.replaceAll(",", "").replace(/[^\d.-]/g, "")) || 0;
const clean = (value: string) => value.replace(/\s+/g, " ").trim();
const iso = (year: string, month: string, day: string) => {
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return date.getUTCMonth() + 1 === Number(month) && date.getUTCDate() === Number(day)
    ? `${year}-${String(Number(month)).padStart(2, "0")}-${String(Number(day)).padStart(2, "0")}`
    : "";
};

export function parseDividendText(text: string, fallbackYear = String(new Date().getFullYear())): ReviewDraft[] {
  const normalized = text.replace(/\r\n?/g, "\n");
  const drafts: ReviewDraft[] = [];
  const tossPattern = /([^\n]+?)\s*배당금\s*([\d,]+(?:\.\d+)?)\s*(원|달러)(?:가|이)?\s*토스증권\s*계좌로\s*입금됐어요[.。]?/g;
  const dates = Array.from(normalized.matchAll(/(?:(\d{4})년\s*)?(\d{1,2})월\s*(\d{1,3})(?:일)?/g));
  const toss = Array.from(normalized.matchAll(tossPattern));
  toss.forEach((match, index) => {
    const start = match.index || 0;
    const previousEnd = index === 0 ? 0 : (toss[index - 1].index || 0) + toss[index - 1][0].length;
    const nextStart = index + 1 < toss.length ? toss[index + 1].index || normalized.length : normalized.length;
    const date = dates.filter(item => (item.index || 0) >= previousEnd && (item.index || 0) <= start).at(-1) || dates.find(item => (item.index || 0) > start && (item.index || 0) < nextStart);
    const rawDay = date?.[3] || "";
    const day = Number(rawDay) <= 31 ? Number(rawDay) : Number(rawDay.slice(-2));
    const paymentDate = date && day >= 1 && day <= 31 ? iso(date[1] || fallbackYear, date[2], String(day)) : "";
    const currency = match[3] === "달러" ? "USD" : "KRW";
    drafts.push({ id: `draft-${start}`, ownerName: "본인", stockName: clean(match[1]).replace(/^배당금\s*입금\s*/i, "").replace(/^(?:(?:\d{4})년\s*)?\d{1,2}월\s*\d{1,3}(?:일)?\s*/, ""), accountName: "토스증권", paymentDate, grossDividend: number(match[2]), tax: 0, netAmount: number(match[2]), currency, notes: "독립형 텍스트 추출 초안", sourceText: match[0], selected: true, reviewReason: paymentDate ? "토스증권 알림에서 추출" : "지급일을 확인해 주세요" });
  });
  const blocks = normalized.split(/(?=\s*(?:최근\s*)?(?:결산\s*)?분배금\s*입금(?:\s*안내)?|\s*ETF\s*분배금\s*입금(?:\s*안내)?|\s*배당금\s*입금(?:\s*안내)?)/i);
  blocks.forEach((block, index) => {
    if (!/(?:\*\s*)?종목명\s*[:：]/.test(block) || !/(?:\*\s*)?입금(?:액|일자)\s*[:：]/.test(block)) return;
    const stockName = clean(block.match(/(?:\*\s*)?종목명\s*[:：]\s*([^\n]+)/)?.[1] || "");
    const accountName = clean(block.match(/(?:\*\s*)?(?:계좌명|증권사)\s*[:：]\s*([^\n]+)/)?.[1] || "");
    const rawDate = block.match(/(?:\*\s*)?입금일자\s*[:：]\s*([^\n]+)/)?.[1] || "";
    const date = rawDate.match(/(\d{4})\D*(\d{1,2})\D*(\d{1,2})/);
    const paymentDate = date ? iso(date[1], date[2], date[3]) : "";
    const rawAmount = block.match(/(?:\*\s*)?입금액\s*[:：]\s*([^\n]+)/)?.[1] || "";
    const netAmount = number(rawAmount);
    drafts.push({ id: `draft-standard-${index}`, ownerName: "본인", stockName, accountName, paymentDate, grossDividend: netAmount, tax: 0, netAmount, currency: "KRW", notes: "독립형 텍스트 추출 초안", sourceText: block.trim(), selected: true, reviewReason: stockName && paymentDate && netAmount ? "증권사 알림에서 추출" : "종목·계좌·지급일·금액을 확인해 주세요" });
  });
  const compactAmountPattern = /\+\s*([\d,]+(?:\.\d+)?)\s*(원|달러)/g;
  const compactAmounts = Array.from(normalized.matchAll(compactAmountPattern));
  compactAmounts.forEach((match, index) => {
    const previousEnd = index ? (compactAmounts[index - 1].index || 0) + compactAmounts[index - 1][0].length : 0;
    const amountStart = match.index || 0;
    const nextLine = normalized.indexOf("\n", amountStart + match[0].length);
    const sameLineTail = normalized.slice(amountStart + match[0].length, nextLine < 0 ? normalized.length : nextLine).trim();
    const stockName = clean(`${normalized.slice(previousEnd, amountStart)} ${sameLineTail}`)
      .replace(/^(?:배당금|분배금)\s*입금\s*/i, "")
      .replace(/[|]/g, " ");
    if (!stockName || /(?:입금|배당금|분배금)\s*$/i.test(stockName)) return;
    const currency = match[2] === "달러" ? "USD" : "KRW";
    const netAmount = number(match[1]);
    if (!netAmount) return;
    drafts.push({ id: `draft-compact-${amountStart}`, ownerName: "본인", stockName, accountName: "", paymentDate: "", grossDividend: netAmount, tax: 0, netAmount, currency, notes: "독립형 줄바꿈 알림 추출 초안", sourceText: `${stockName}\n${match[0]}`.trim(), selected: true, reviewReason: "줄바꿈형 증권 알림에서 추출 · 계좌와 지급일을 확인해 주세요" });
  });
  return drafts;
}
